/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectIsActionsBlocked,
  selectIsAdmin,
  selectIsOwner,
  showNotifier
} from 'containers/app/appSlice';
import { checkFeatureStatus } from '../card-on-file/utils';
import SidePanel from 'components/side-panel/SidePanel';
import { SharePendingInvoiceMainSection } from './SharePendingInvoiceMainSection';
import { useLazyDownloadInvoiceAsPDFQuery } from '../api';
import { useGetInvoiceDataById } from '../hooks/useGetInvoiceDataById';
import { ArrowRightIcon, CrossIconvector } from 'assets/icons';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { SidePanelRouteState } from 'routes/types';
import ManualKeyInModal from '../manual-key-in/ManualKeyInModal';
import { NotifierTypes } from 'constants/notifierConstants';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { formatAmount, formatDecimal } from 'utils/amountUtils';
import { PaymentMethods } from 'constants/common';
import MerchantOrTeamBlockedInfoPanel from 'components/merchant-or-team-blocked/MerchantOrTeamBlockedInfoPanel';
import { useLazyGetCustomerCardsQuery } from 'containers/customers/api';
import routesPath from 'routes/RoutesPath';

export const SharePendingInvoice: React.FC = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const isOwner = useAppSelector(selectIsOwner);
  const isAdmin = useAppSelector(selectIsAdmin);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    invoiceId,
    rootInvoiceId,
    rootReceiptId,
    rootPage,
    rootCustomerId,
    rootDisputeId,
    rootJobId,
    rootEstimateId
  } = useParams();
  interface OutletContext {
    handleSidePanelClose: () => void;
  }

  const { handleSidePanelClose } = useOutletContext<OutletContext>();

  const [downloadInvoice] = useLazyDownloadInvoiceAsPDFQuery();
  const {
    invoiceData,
    isSuccess: invoiceDataSuccess,
    isLoading: fetchingInvoiceData
  } = useGetInvoiceDataById(invoiceId || rootInvoiceId);
  const {
    data: merchantCustomisations,
    isSuccess: merchantCustomisationsSuccess,
    isFetching: merchantCustomisationsFetching
  } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });
  const [
    getCustomerCards,
    { data: customerCardsDataResponse, isFetching: isLoadingCustomerCards, isSuccess: isSuccessCustomerCards }
  ] = useLazyGetCustomerCardsQuery();
  const customerCardsData = customerCardsDataResponse || [];

  const { paymentModes } = merchantCustomisations ?? {};
  const [isRootState, setIsRootState] = useState(true);
  const [openManualKeyInModal, setOpenManualKeyInModal] = useState<boolean>(false);

  useEffect(() => {
    if (invoiceData && invoiceData.invoiceCustomer?.id && !isSuccessCustomerCards) {
      getCustomerCards({
        teamId: currentTeam.id,
        customerId: invoiceData.invoiceCustomer.id
      });
    }
  }, [invoiceData, currentTeam.id, getCustomerCards]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState: SidePanelRouteState = location.state ?? {};
    if (routeState?.fromSidePanel) {
      setIsRootState(false);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  const handleSidePanelBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else {
      navigate(-1);
    }
  };

  const handlePayWithQRCodeClick = () => {
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          `/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
          {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          }
        );
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/share-qr`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootPage) {
      navigate(
        `/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootReceiptId) {
      navigate(
        `/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootCustomerId) {
      navigate(
        `/customers/${currentTeam?.id}/${rootCustomerId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootDisputeId) {
      navigate(
        `/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(
        `/estimates/${currentTeam?.id}/${rootEstimateId}/share-pending-invoice/${invoiceData?.invoiceId}/share-qr`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    }
  };

  const handleInvoiceShareClick = () => {
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          `/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
          {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          }
        );
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/share-url`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else if (rootPage) {
      navigate(
        `/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootReceiptId) {
      navigate(
        `/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootCustomerId) {
      navigate(
        `/customers/${currentTeam?.id}/${rootCustomerId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootDisputeId) {
      navigate(
        `/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(
        `/estimates/${currentTeam?.id}/${rootEstimateId}/share-pending-invoice/${invoiceData?.invoiceId}/share-url`,
        {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        }
      );
    }
  };

  const handleShareInvoiceOnPaymentSuccess = () => {
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice/${invoiceData?.invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE,
          replace: true
        });
      } else {
        navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE,
          replace: true
        });
      }
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-invoice/${invoiceData?.invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE,
        replace: true
      });
    }
  };

  const handleManualKeyIn = () => {
    const manualKeyIn = paymentModes.find(mode => mode.name === PaymentMethods.MANUAL_KEY_IN);
    const featureStatus = checkFeatureStatus(manualKeyIn, isOwner, isAdmin, 'manualKeyIn');

    if (!featureStatus.isEnabled) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: featureStatus.message
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
      return;
    }

    if (+invoiceData.pendingAmount <= +manualKeyIn.maximumAmount) {
      setOpenManualKeyInModal(true);
    } else {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: `Manual card entry can't be used for this payment. 
                            It's available only for amounts up to 
                            $${formatDecimal(formatAmount(+manualKeyIn.maximumAmount))}. 
                            Please use a different method or contact support for help.`
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const handleMarkAsFullyPaid = () => {
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      routedFromSecondaryPanel: true
    };
    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          routesPath.INVOICE_RECORD_PAYMENT_INVOICE.replace(':teamId', currentTeam?.id.toString() || '')
            .replace(':rootInvoiceId', rootInvoiceId)
            .replace(':invoiceId', invoiceId),
          { state: routeState }
        );
      } else {
        navigate(
          routesPath.INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '').replace(
            ':rootInvoiceId',
            rootInvoiceId
          ),
          { state: routeState }
        );
      }
    } else if (rootJobId) {
      navigate(
        routesPath.JOB_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootJobId', rootJobId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/record-payment/${invoiceId}`, {
        state: routeState
      });
    } else if (rootReceiptId) {
      navigate(
        routesPath.RECEIPT_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootReceiptId', rootReceiptId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootCustomerId) {
      navigate(
        routesPath.CUSTOMER_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootCustomerId', rootCustomerId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootDisputeId) {
      navigate(
        routesPath.DISPUTE_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootDisputeId', rootDisputeId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    } else if (rootEstimateId) {
      navigate(
        routesPath.ESTIMATE_INVOICE_RECORD_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
          .replace(':rootEstimateId', rootEstimateId)
          .replace(':invoiceId', invoiceId),
        { state: routeState }
      );
    }
  };

  const downloadFile = async () => {
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading started in the background',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    await downloadInvoice({ invoiceId: invoiceData.invoiceId });
  };

  const handleManualKeyInClose = () => {
    setOpenManualKeyInModal(false);
  };

  const handleCollectPartialAmountClick = () => {
    // Navigate to collect payment panel with showEditAmount flag
    const routeState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      routedFromSecondaryPanel: true,
      counter: 1,
      showEditAmount: true // Flag to show edit amount view in CollectPaymentPanel
    };

    if (rootInvoiceId) {
      if (invoiceId) {
        navigate(
          routesPath.INVOICE_SHARE_PENDING_INVOICE_COLLECT_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '')
            .replace(':rootInvoiceId', rootInvoiceId)
            .replace(':invoiceId', invoiceId),
          { state: routeState }
        );
      } else {
        navigate(
          routesPath.INVOICE_PENDING_SHARE_COLLECT_PAYMENT.replace(':teamId', currentTeam?.id.toString() || '').replace(
            ':rootInvoiceId',
            rootInvoiceId
          ),
          { state: routeState }
        );
      }
    } else if (rootReceiptId) {
      navigate(
        `/payments/receipts/${currentTeam?.id}/${rootReceiptId}/invoice/${invoiceData?.invoiceId}/collect-payment`,
        {
          state: routeState
        }
      );
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/invoice/${invoiceData?.invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootDisputeId) {
      navigate(
        `/payments/disputes/${currentTeam?.id}/${rootDisputeId}/invoice/${invoiceData?.invoiceId}/collect-payment`,
        {
          state: routeState
        }
      );
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/invoice/${invoiceData?.invoiceId}/collect-payment`, {
        state: routeState
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/invoice/${invoiceData?.invoiceId}/collect-payment`, {
        state: routeState
      });
    }
  };

  return (
    <>
      <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
        {isActionsBlocked ? (
          <MerchantOrTeamBlockedInfoPanel onBack={handleSidePanelBack} />
        ) : (
          <div className="h-full w-96 px-[26px] pt-[30px]">
            {(fetchingInvoiceData || merchantCustomisationsFetching || isLoadingCustomerCards) && (
              <div className="flex h-full w-full items-center justify-center">
                <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
              </div>
            )}
            {invoiceDataSuccess && isSuccessCustomerCards && merchantCustomisationsSuccess && (
              <>
                <div
                  className="mb-10 flex cursor-pointer items-center gap-1.5 text-17px font-semibold text-primaryText"
                  onClick={handleSidePanelBack}>
                  {isRootState ? <CrossIconvector className="shrink-0" /> : <ArrowRightIcon className="rotate-180" />}
                  Share invoice {invoiceData?.invoiceNumber}
                </div>
                <SharePendingInvoiceMainSection
                  invoiceData={invoiceData}
                  handlePayWithQRCodeClick={handlePayWithQRCodeClick}
                  handleRecordPayment={handleMarkAsFullyPaid}
                  handleManualKeyIn={handleManualKeyIn}
                  handleSendInvoice={handleInvoiceShareClick}
                  handleDownloadInvoice={downloadFile}
                  customerCardsData={customerCardsData}
                  isLoadingCards={isLoadingCustomerCards}
                  closeSuccessModalCallback={handleSidePanelBack}
                  paymentModes={paymentModes}
                  handleSendInvoiceOnPaymentSuccess={handleShareInvoiceOnPaymentSuccess}
                  handleCollectPartialAmountClick={handleCollectPartialAmountClick}
                />
              </>
            )}
          </div>
        )}
      </SidePanel>
      {openManualKeyInModal && (
        <ManualKeyInModal
          onClose={handleManualKeyInClose}
          invoiceId={invoiceData.invoiceId}
          checkoutUrl={invoiceData.checkoutURL}
        />
      )}
    </>
  );
};

export default SharePendingInvoice;
